/**
 * Cognito Hosted UI configuration consumed by react-oidc-context's
 * AuthProvider. Code+PKCE flow, no client secret (SPA).
 *
 * Hosted UI logout has a Cognito-specific URL shape (not OIDC standard)
 * — we override `signoutRedirect` below to issue the right one.
 */

import { WebStorageStateStore } from 'oidc-client-ts';
import type { User, UserManagerSettings } from 'oidc-client-ts';

import { sanitizeReturnTo } from './authOptions';

const HOSTED_UI = import.meta.env.VITE_COGNITO_HOSTED_UI;
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID;
// DE-127: derive logout URL from runtime origin so the same bundle works
// when served from cloudfront.net OR demoexp.artome.fi without mismatched
// redirects. Build-time env var still wins when explicitly set (useful for
// localhost dev hitting a stage-specific Hosted UI).
const LOGOUT_REDIRECT =
  import.meta.env.VITE_COGNITO_LOGOUT_REDIRECT || `${window.location.origin}/`;

export const oidcConfig: UserManagerSettings & {
  onSigninCallback?: (user?: User) => void;
} = {
  authority: import.meta.env.VITE_COGNITO_AUTHORITY,
  client_id: CLIENT_ID,
  redirect_uri: `${window.location.origin}/auth/callback`,
  response_type: 'code',
  scope: 'openid email profile',
  // Persist the authenticated User (incl. the rotating 30-day refresh token)
  // in localStorage so a returning user is silently re-authenticated across
  // browser restarts (DE-182 / auth-simplification) — sessionStorage, the
  // oidc-client default, is wiped on tab/browser close and threw the refresh
  // token away every session. Refresh-token ROTATION is enabled on the Cognito
  // app client (aws-setup/cognito/apply-cognito.sh), which is the IETF
  // "OAuth 2.0 for Browser-Based Apps" BCP requirement for browser-stored
  // tokens. (Only the User store needs to persist; the transient per-flow
  // PKCE state store stays in its sessionStorage default.) Moving tokens fully
  // out of JS (HTTP-only cookies via a BFF) is a deliberate future hardening
  // step, tracked separately.
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  // Renew the access/ID token from the refresh token shortly before it expires
  // while the app is open; on cold load, AuthGate triggers a one-shot silent
  // renewal when a stored-but-expired session is found.
  automaticSilentRenew: true,
  loadUserInfo: false, // Cognito ID token already carries the claims we need
  // After successful sign-in, leave the callback route and restore the page
  // the user originally requested (DE-169 — round-tripped through the OIDC
  // `state`, defaulting to '/'). `replaceState` alone changes the URL bar but
  // doesn't fire popstate, so React Router stays on /auth/callback and
  // AuthCallback keeps rendering "Signing in…"; a full navigation forces both
  // the URL and the route tree to update. returnTo is re-sanitised
  // (same-origin only) before use even though it originated locally.
  onSigninCallback: (user?: User) => {
    const returnTo = sanitizeReturnTo(
      (user?.state as { returnTo?: string } | undefined)?.returnTo,
    );
    window.location.href = returnTo;
  },
};

export function hostedUiLogoutUrl(): string {
  const u = new URL(`${HOSTED_UI}/logout`);
  u.searchParams.set('client_id', CLIENT_ID);
  u.searchParams.set('logout_uri', LOGOUT_REDIRECT);
  return u.toString();
}
